import ThaloLogo from "@/assets/thalo-light.png";
import NextEra from "@/assets/nextera.png";
import Mitsubishi from "@/assets/mitsubishi.png";
import ThaloIcon from "@/assets/thalo_icon.svg";
import {
  AlertCircle,
  Bell,
  BriefcaseBusiness,
  ChevronDown,
  ChevronUp,
  Cross,
  Earth,
  Eye,
  FerrisWheel,
  Lock,
  LogOut,
  PieChart,
  Sparkles,
  Mail,
  Table2, Target, TestTubeDiagonal,
  TrendingUp,
  User as UserIcon,
  Users,
  PackagePlus
} from "lucide-react";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useUser } from "@/api/api";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { hasOrgPermission, isInstallerOnly } from "@/user/user";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import React, { useEffect, useState } from "react";
import { Link, useLocation, useMatchRoute, useNavigate } from "@tanstack/react-router";
import { useEquipment } from "@/equipment/api";
import { Switch } from "@/components/ui/switch";
import { ANONYMIZE_USER_PERMISSION, useAnonymize } from "@/anonymization/useAnonymize";
import { logout } from "@/auth";
import { OnlyWithUserPermissionOrStaff } from "@/user/OnlyWithUserPermissionOrStaff";
import { Collapsible, CollapsibleContent } from "./ui/collapsible";
import { CollapsibleTrigger } from "@/components/ui/collapsible";
import StaffOnly from "@/user/StaffOnly";
import { QAOnly } from "@/components/QAOnly";
import { useOrganizationMinimal, useOrganizationsMinimal, useOrganizationIdWithFallback } from "@/organization/api";
import { useLocations } from "@/location/api";
import { Spinner } from "@/components/ui/spinner";
import { Suspense } from "react";
import { UserProfileDialog } from "@/components/UserProfileDialog";

const SidebarButton = ({ link, name, Icon, fuzzyMatch = false }: {
  link: string,
  name: string | React.ReactNode,
  Icon?: any,
  fuzzyMatch?: boolean
}) => {
  const matchRoute = useMatchRoute()
  const selected = matchRoute({ to: link, fuzzy: fuzzyMatch });

  return (
    <SidebarMenuButton asChild isActive={!!selected} tooltip={typeof name === 'string' ? name : 'Trends'} className="h-auto">
      <Link to={link} className="w-full justify-start flex">
        {
          Icon && (
            <Icon size={18} className="mr-2 "/>
          )
        }
        {name}
      </Link>
    </SidebarMenuButton>
  );
};

const UserNav = () => {

  const { open, isMobile } = useSidebar();
  const user = useUser();
  const navigate = useNavigate();

  const [anonymize, setAnonymize] = useAnonymize();
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  const isNameIncomplete = !user.data?.first_name || !user.data?.last_name;

  return (
    <>
      <DropdownMenu modal={isMobile}>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton>
            <div className="text-black px-0 relative rounded-full">
              <Avatar className="h-[18px] w-[18px] text-[10px]">
                <AvatarFallback>
                  {user.data?.first_name && user.data?.last_name
                    ? `${user.data?.first_name
                      .charAt(0)
                      .toUpperCase()}${user.data?.last_name
                      .charAt(0)
                      .toUpperCase()}`
                    : user.data?.first_name
                      ? user.data?.first_name.charAt(0).toUpperCase()
                      : user.data?.username.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              {isNameIncomplete && (
                <Badge className="absolute -right-1 -top-1 h-4 w-4 p-0 flex items-center justify-center" variant="yellow">
                  <AlertCircle className="h-3 w-3" />
                </Badge>
              )}
            </div>
            {
              (user.data?.first_name && user.data?.last_name)
                ? <span>{user.data?.first_name} {user.data?.last_name}</span>
                : <span>{user.data?.username}</span>
            }
            <ChevronUp className="ml-auto"/>
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side={"top"}
          className={`w-[--radix-popper-anchor-width] ${!open ? "ml-4" : ""}`}
        >
          <DropdownMenuItem onClick={() => setIsProfileDialogOpen(true)} className="cursor-pointer relative">
            <UserIcon className="mr-2 h-4 w-4"/>
            <span>Edit Profile</span>
            {isNameIncomplete && (
              <Badge className="ml-auto h-4 w-4 p-0 flex items-center justify-center" variant="yellow">
                <AlertCircle className="h-3 w-3" />
              </Badge>
            )}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate({ to: "/settings/notifications" })} className="cursor-pointer">
            <Bell className="mr-2 h-4 w-4"/>
            <span>Notification Settings</span>
          </DropdownMenuItem>
          <OnlyWithUserPermissionOrStaff permission={ANONYMIZE_USER_PERMISSION}>
            <DropdownMenuItem>
              <Switch
                className="mr-2"
                checked={anonymize}
                onCheckedChange={(value: boolean) =>
                  setAnonymize(value)
                }
              />
              <span>Anonymize</span>
            </DropdownMenuItem>
          </OnlyWithUserPermissionOrStaff>
          <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
            <LogOut className="mr-2 h-4 w-4"/>
            <span>Log out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <UserProfileDialog
        isOpen={isProfileDialogOpen}
        onOpenChange={setIsProfileDialogOpen}
      />
    </>
  )
}

const LocationListFallback = () => {
  return (
    <SidebarMenuSub>
      <SidebarMenuSubItem>
        <div className="flex items-center justify-center py-3 px-2">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Spinner className="h-4 w-4" />
            <span className="text-sm">Loading locations...</span>
          </div>
        </div>
      </SidebarMenuSubItem>
    </SidebarMenuSub>
  );
};

const EquipmentSidebarItem = ({ equipmentId, link }: { equipmentId: number; link: string }) => {
  const { data: equipmentData } = useEquipment(equipmentId);
  return (
    <SidebarMenuSub className="pr-0 mr-0">
      <SidebarMenuSubItem>
        <SidebarMenuButton asChild>
          <SidebarButton link={link} name={equipmentData.name} />
        </SidebarMenuButton>
      </SidebarMenuSubItem>
    </SidebarMenuSub>
  );
};

const LocationList = ({ organizationId }: { organizationId: number }) => {
  const { data: { results: locations } } = useLocations({ filters: { organization_id: organizationId }});
  const matchRoute = useMatchRoute();
  const isOnEquipmentPage = matchRoute({ to: "/organization/$organizationId/locations/$locationId/equipment/$equipmentId" })

  return (
    <SidebarMenuSub>
      {
        locations.map((location) => (
          <SidebarMenuSubItem key={location.id}>
            <SidebarMenuButton asChild>
              <SidebarButton
                link={`/organization/${organizationId}/locations/${location.id}`}
                name={location.name}
              />
            </SidebarMenuButton>
            {
              //@ts-ignore
              !!isOnEquipmentPage && isOnEquipmentPage.locationId === location.id.toString() && (
                <Suspense fallback={null}>
                  <EquipmentSidebarItem
                    //@ts-ignore
                    equipmentId={isOnEquipmentPage.equipmentId}
                    link={`/organization/${organizationId}/locations/${location.id}/equipment/${isOnEquipmentPage.equipmentId}`}
                  />
                </Suspense>
              )
            }
          </SidebarMenuSubItem>
        ))
      }
    </SidebarMenuSub>
  );
};

const NoOrganizationSidebarContent = () => {
  return (
    <SidebarContent>
      <SidebarGroup className="text-white">
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton disabled>
                <BriefcaseBusiness />
                <span>No organizations</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    </SidebarContent>
  );
};

const OrganizationSidebarContent = ({ organizationId }: { organizationId: number }) => {
  const { open } = useSidebar();
  const matchRoute = useMatchRoute();
  const location = useLocation();
  const user = useUser();

  const isOnLocationsPages = matchRoute({ to: "/organization/$organizationId/locations", fuzzy: true });

  const [locationsListExpanded, setLocationsListExpanded] = useState(!!isOnLocationsPages);
  const [staffRoutesListExpanded, setStaffRoutesListExpanded] = useState(false);

  useEffect(() => {
    setLocationsListExpanded(!!isOnLocationsPages);
  }, [!!isOnLocationsPages]);

  useEffect(() => {
    setStaffRoutesListExpanded(false);
  }, [location]);

  const isInstaller = user.data && isInstallerOnly(user.data, organizationId);

  return (
    <>
      <SidebarContent>
        <SidebarGroup className="text-white">
          <SidebarGroupContent>
            <SidebarMenu>
              {!isInstaller && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <SidebarButton
                      link={`/organization/${organizationId}/`}
                      name="Overview"
                      Icon={Target}
                    />
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              {!isInstaller && (
                <Collapsible open={locationsListExpanded} onOpenChange={setLocationsListExpanded}
                             className="group/collapsible">
                  <SidebarMenuItem>
                    <div className="flex flex-row gap-2">
                      <SidebarMenuButton asChild>
                        <SidebarButton
                          link={`/organization/${organizationId}/locations/`}
                          name="Locations"
                          Icon={Earth}
                        />
                      </SidebarMenuButton>
                      {
                        open && (
                          <CollapsibleTrigger>
                            <ChevronDown
                              className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180"
                            />
                          </CollapsibleTrigger>
                        )
                      }
                    </div>
                    <CollapsibleContent>
                      {locationsListExpanded && (
                        <Suspense fallback={<LocationListFallback />}>
                          <LocationList organizationId={organizationId} />
                        </Suspense>
                      )}
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              )}
              {!isInstaller && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <SidebarButton
                      link={`/organization/${organizationId}/issues`}
                      name="Issue Management"
                      Icon={Sparkles}
                      fuzzyMatch={true}
                    />
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <SidebarButton
                    link={`/organization/${organizationId}/install-flow`}
                    name="Install"
                    Icon={PackagePlus}
                  />
                </SidebarMenuButton>
              </SidebarMenuItem>
              {!isInstaller && user.data && hasOrgPermission(user.data, organizationId, 'sensorkit_meta.list_organization_members') && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <SidebarButton
                      link={`/organization/${organizationId}/users`}
                      name="Users"
                      Icon={Users}
                    />
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <StaffOnly>
        <SidebarFooter className="text-white bg-white bg-opacity-20">
          <SidebarMenu className="">
            <Collapsible open={staffRoutesListExpanded} onOpenChange={setStaffRoutesListExpanded}
                         className="group/collapsible">
              <SidebarMenuItem>
                <CollapsibleContent>
                  <SidebarGroupContent className="pb-2 flex flex-col gap-1">
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild>
                        <SidebarButton
                          link={`/all-organizations`}
                          name="All organizations"
                          Icon={Earth}
                        />
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild>
                        <SidebarButton
                          link={`/issue-management`}
                          name="Issue management"
                          Icon={FerrisWheel}
                        />
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild>
                        <SidebarButton
                          link={`/sensor-kit-health`}
                          name="Sensor kit health"
                          Icon={Cross}
                        />
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild>
                        <SidebarButton
                          link={`/sensor-kit-health-new`}
                          name="Sensor kit health V2"
                          Icon={Cross}
                        />
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild>
                        <SidebarButton
                          link={`/trends-beta`}
                          name={
                            <div className="flex items-center gap-2">
                              <span>Trends</span>
                              <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">BETA</span>
                            </div>
                          }
                          Icon={TrendingUp}
                        />
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild>
                        <SidebarButton
                          link={`/engineering-charts`}
                          name="Engineering Charts"
                          Icon={PieChart}
                        />
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild>
                        <SidebarButton
                          link={`/metrics`}
                          name="Metrics"
                          Icon={Table2}
                        />
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild>
                        <SidebarButton
                          link={`/meters`}
                          name="Meter monitoring"
                          Icon={Eye}
                          fuzzyMatch={true}
                        />
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild>
                        <SidebarButton
                          link={`/validate-sensor-placement`}
                          name="Validate sensor placement"
                          Icon={TestTubeDiagonal}
                          fuzzyMatch={true}
                        />
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild>
                        <SidebarButton
                          link={`/email-concierge`}
                          name="Email Concierge"
                          Icon={Mail}
                        />
                      </SidebarMenuButton>
                    </SidebarMenuItem>

                  </SidebarGroupContent>
                </CollapsibleContent>
                <CollapsibleTrigger asChild>
                  <div className="flex flex-row gap-2">
                    <SidebarMenuButton>
                      <Lock/>
                      <p>Staff only</p>
                      {
                        open && (
                          <ChevronUp
                            className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180"
                          />
                        )
                      }
                    </SidebarMenuButton>
                  </div>
                </CollapsibleTrigger>

              </SidebarMenuItem>
            </Collapsible>
          </SidebarMenu>
        </SidebarFooter>
      </StaffOnly>
    </>
  );
};

const OrganizationSelector = ({ organizationId }: { organizationId: number }) => {
  const { data: { results: allOrganizations } } = useOrganizationsMinimal();
  const { data: selectedOrganization } = useOrganizationMinimal(organizationId);
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();

  const hasMultipleOrgs = allOrganizations.length > 1;

  if (!hasMultipleOrgs) {
    return (
      <div className="flex w-full items-center gap-2 rounded-md p-2 text-sm">
        <BriefcaseBusiness className="h-4 w-4"/>
        <span className="truncate">{selectedOrganization.name}</span>
      </div>
    );
  }

  return (
    <>
      <SidebarMenuButton onClick={() => setDialogOpen(true)} className="h-auto">
        <BriefcaseBusiness />
        {selectedOrganization.name}
        <ChevronUp className="ml-auto" />
      </SidebarMenuButton>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md p-0">
          <DialogHeader className="px-6 pt-6 pb-0">
            <DialogTitle>Switch Organization</DialogTitle>
          </DialogHeader>
          <Command>
            <CommandInput placeholder="Search organizations..." className="border-0 border-b rounded-none" />
            <CommandList className="max-h-[300px]">
              <CommandEmpty>No organization found.</CommandEmpty>
              <CommandGroup className="p-2">
                {allOrganizations.map((organization) => (
                  <CommandItem
                    key={organization.id}
                    value={organization.name}
                    onSelect={() => {
                      setDialogOpen(false);
                      navigate({ to: '/organization/$organizationId', params: { organizationId: organization.id } });
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedOrganization.id === organization.id ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {organization.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>
    </>
  );
}

export const AppSidebar = () => {

  const { open } = useSidebar();
  const organizationId = useOrganizationIdWithFallback();

  return (
    <Sidebar className="border-none" collapsible="icon">
      <SidebarHeader className="px-4 py-0">
        {organizationId ? (
          <Link to="/organization/$organizationId" params={{ organizationId }}
                className="relative">
            {
              open
                ? ![45, 15].includes(organizationId)
                  ? <img src={ThaloLogo} className="w-[140px] py-6 self-center"/>
                  : (
                    <div className="py-6">
                      <img src={organizationId === 45 ? NextEra : Mitsubishi} className="w-[140px] mb-2 self-center brightness-0 invert"/>
                      <div className="flex items-center gap-2">
                        <span className="text-white text-sm">Powered by</span>
                        <img src={ThaloLogo} className="h-6" />
                      </div>
                    </div>
                  )
                : <img src={ThaloIcon} className="w-[140px] py-6 self-center"/>
            }
            <QAOnly>
              <Badge className={`absolute ${open ? "right-4 top-12" : "-left-3 -bottom-1"}`} variant="yellow">
                QA
              </Badge>
            </QAOnly>
          </Link>
        ) : (
          <div className="relative">
            {
              open
                ? <img src={ThaloLogo} className="w-[140px] py-6 self-center"/>
                : <img src={ThaloIcon} className="w-[140px] py-6 self-center"/>
            }
            <QAOnly>
              <Badge className={`absolute ${open ? "right-4 top-12" : "-left-3 -bottom-1"}`} variant="yellow">
                QA
              </Badge>
            </QAOnly>
          </div>
        )}
      </SidebarHeader>

      {organizationId ? <OrganizationSidebarContent organizationId={organizationId} /> : <NoOrganizationSidebarContent />}

      <SidebarFooter className="text-white">
        <SidebarMenu>
          <SidebarMenuItem>
            <UserNav/>
          </SidebarMenuItem>
          {organizationId && (
            <SidebarMenuItem>
              <OrganizationSelector organizationId={organizationId} />
            </SidebarMenuItem>
          )}
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
};
